import {defineStore} from "pinia";
import {SearchApi} from "@/ api/SearchApi";
import {ClientsApi} from "@/ api/ClientsApi";
import {CommonEntityRecord} from "@/models/interfaces/common";
import {mockClients} from "@/mock/clients";
import {highlightSearchText} from "@/utils/textHelpers";

function highlightParameter(param: Parameter, filter: string): Parameter {
    if (param.Type === 0 && param.Instance) {
        if (param.Instance.IsRecord) {
            return {
                ...param,
                Instance: {
                    ...param.Instance,
                    RecordFields: param.Instance.RecordFields
                        ? param.Instance.RecordFields.map((f: Parameter) => highlightParameter(f, filter))
                        : param.Instance.RecordFields,
                },
            }
        }
        if (param.UsedInSearch && param.Instance.Value) {
            return {
                ...param,
                Instance: {
                    ...param.Instance,
                    Value: highlightSearchText(filter, param.Instance.Value),
                },
            }
        }
        return param
    }
    if (param.Type === 1 && param.ListTitle) {
        const instances = param.ListTitle.Instances || []
        return {
            ...param,
            ListTitle: {
                ...param.ListTitle,
                Instances: instances.map((inst: Instance) => {
                    if (inst.IsRecord) {
                        return {
                            ...inst,
                            RecordFields: inst.RecordFields
                                ? inst.RecordFields.map((f: Parameter) => highlightParameter(f, filter))
                                : inst.RecordFields,
                        }
                    }
                    if (param.UsedInSearch && inst.Value) {
                        return {
                            ...inst,
                            Value: highlightSearchText(filter, inst.Value),
                        }
                    }
                    return inst
                }),
            },
        }
    }
    return param
}

function highlightProjects(projects: Project[], filter?: string): Project[] {
    if (!filter) return projects
    return projects.map(p => ({
        ...p,
        Name: highlightSearchText(filter, p.Name),
        Clients: p.Clients
            ? p.Clients.map(c => ({...c, Name: highlightSearchText(filter, c.Name)}))
            : p.Clients,
        Tags: p.Tags
            ? p.Tags.map(tag => ({...tag, Name: highlightSearchText(filter, tag.Name)}))
            : p.Tags,
        Parameters: p.Parameters
            ? p.Parameters.map(param => highlightParameter(param, filter))
            : p.Parameters,
    }))
}

export type SearchState = {
    isClientsLoading: boolean,
    isProjectsLoading: boolean,
    clients: SearchedClient[],
    clientsHighlighted: SearchedClient[],
    canLoadNextClients: boolean,
    clientsCount: number,
    projects: Project[],
    canLoadNextProjects: boolean,
    projectCount: number,
    stageSets: any[],
    selectedStage: number | null
    selectedStages: number[]
    selectedTags: number[]
    clientFilterList: ClientFilterOption[]
    selectedClients: SelectedFilterClient[]
    byBase: boolean
    actualRoles: CommonEntityRecord[]
    isClientFilterLoading: boolean
    pendingFileSearch: FileSearchRequest | null
    pendingJobNavigation: JobNavigationRequest | null
    jobPanelFocusRequest: number
};

export type ClientFilterUnion = {
    Id: number,
    Name: string,
    Count: number,
}

export type ClientFilterOption = {
    Id: number,
    Name: string,
    Count: number,
    Union: ClientFilterUnion[],
}

export type SelectedFilterClient = {
    Id: number,
    Name: string,
    UseUnion: boolean,
    Roles: CommonEntityRecord[],
}

export type FileSearchRequest = {
    type: "client" | "project",
    id: number,
    fileName: string,
    fileId: number;
};

export type JobNavigationRequest = {
    type: "client" | "project",
    entityId: number,
    jobId: number,
};

export type Project = {
    Clients: Client[],
    Color: string,
    CreateTime: string
    Id: number
    Name: string
    Parameters: Parameter[],
    Tags: Tag[]
}

export const searchStore = defineStore({
    id: 'searchStore',
    state: () => ({
        isClientsLoading: true,
        isProjectsLoading: true,
        clients: [],
        clientsHighlighted: [],
        canLoadNextClients: true,
        clientsCount: 0,
        projects: [],
        canLoadNextProjects: true,
        projectCount: 0,
        stageSets: [],
        selectedStage: null,
        selectedStages: [],
        selectedTags: [],
        clientFilterList: [],
        selectedClients: [],
        byBase: false, // флаг "По базе"
        actualRoles: [],
        isClientFilterLoading: false,
        pendingFileSearch: null,
        pendingJobNavigation: null,
        jobPanelFocusRequest: 0
    } as SearchState),
    actions: {
        getClients() {
            this.clients = mockClients
        },
        requestFileSearch(req: FileSearchRequest) {
            this.pendingFileSearch = req
        },
        clearPendingFileSearch() {
            this.pendingFileSearch = null
        },
        requestJobNavigation(req: JobNavigationRequest) {
            this.pendingJobNavigation = req
        },
        clearPendingJobNavigation() {
            this.pendingJobNavigation = null
        },
        requestJobPanelFocus() {
            this.jobPanelFocusRequest++
        },
        async getInitClients() {
            try {
                this.isClientsLoading = true
                const initValues = await SearchApi.getInitClients()
                this.clients = initValues.Clients
                this.clientsHighlighted = this.highlightClients(initValues.Clients, '')
                this.clientsCount = initValues.ClientCount
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        },
        async getClientsStartPack(filter?: string, tags?: number[]) {
            try {
                this.isClientsLoading = true
                const initValues = await SearchApi.getInitClients(filter, tags)
                this.clients = initValues.Clients
                this.clientsHighlighted = this.highlightClients(initValues.Clients, filter)
                this.clientsCount = initValues.ClientCount
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        },
        async getClientsPartial(filter?: string, tags?: number[]) {
            try {
                this.isClientsLoading = true
                if (this.canLoadNextClients) {
                    const initValues = await SearchApi.getClientsPartial(filter, tags, this.clients[this.clients.length - 1].CreateTime?.split('+')[0])
                    this.clients = [...this.clients, ...initValues.Clients]
                    this.clientsHighlighted = [...this.clientsHighlighted, ...this.highlightClients(initValues.Clients, filter)]
                    if (initValues.Clients.length < 30) {
                        this.canLoadNextClients = false
                    }
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.isClientsLoading = false
            }
        },
        removeClientFromList(clientId: number) {
            const topIdx = this.clients.findIndex(c => c.Id === clientId)
            if (topIdx !== -1) {
                this.clients.splice(topIdx, 1)
                if (this.clientsCount > 0) {
                    this.clientsCount -= 1
                }
            }
            const topHighIdx = this.clientsHighlighted.findIndex(c => c.Id === clientId)
            if (topHighIdx !== -1) {
                this.clientsHighlighted.splice(topHighIdx, 1)
            }

            for (const c of this.clients) {
                if (!c.Clients) continue

                const i = c.Clients.findIndex(cc => cc.Id === clientId)
                if (i !== -1) {
                    c.Clients.splice(i, 1)
                }
            }
            for (const c of this.clientsHighlighted) {
                if (!c.Clients) continue

                const i = c.Clients.findIndex(cc => cc.Id === clientId)
                if (i !== -1) {
                    c.Clients.splice(i, 1)
                }
            }

            for (const p of this.projects) {
                if (!p.Clients) continue

                const i = p.Clients.findIndex(c => c.Id === clientId)
                if (i !== -1) {
                    p.Clients.splice(i, 1)
                }
            }
        },

        highlightClients(highlightClients: SearchedClient[], filter?: string) {
            if (!filter) {
                return highlightClients
            }

            return highlightClients.map(client => {
                return {
                    ...client,
                    Name: highlightSearchText(filter, client.Name),
                    AddressChain: client.AddressChain
                        ? client.AddressChain.map(address => highlightSearchText(filter, address))
                        : client.AddressChain,
                    Clients: client.Clients
                        ? client.Clients.map(c => ({
                            ...c,
                            Name: highlightSearchText(filter, c.Name)
                        }))
                        : client.Clients,
                    Parameters: client.Parameters?.length
                        ? client.Parameters.map(p =>
                            p.Instance?.Value
                                ? {
                                    ...p,
                                    Instance: {
                                        ...p.Instance,
                                        Value: highlightSearchText(filter, p.Instance?.Value)
                                    }
                                }
                                : p
                        )
                        : client.Parameters,
                    Tags: client.Tags?.map(tag => ({
                        ...tag,
                        Name: highlightSearchText(filter, tag.Name),
                    })),
                }
            }) as SearchedClient[]
        },
        async getInitProjects() {
            try {
                this.isProjectsLoading = true
                const initValues = await SearchApi.getInitProjects()
                this.projects = initValues.Projects
                this.stageSets = initValues.StageSets
                this.projectCount = initValues.ProjectCount
                this.selectedStage = -1
            } catch (e) {
                console.error(e)
            } finally {
                this.isProjectsLoading = false
            }
        },
        buildSearchClients() {
            return this.byBase
                ? []
                : this.selectedClients.map(c => ({
                    id: c.Id,
                    roles: c.Roles.map(r => r.Id),
                    useUnion: c.UseUnion,
                }))
        },
        async getFilteredProjects(nameFilter: string = '') {
            try {
                this.isProjectsLoading = true
                const clients = this.buildSearchClients()
                const values = await SearchApi.getProjects(this.selectedStages, this.selectedTags, nameFilter, clients)
                this.projects = highlightProjects(values.Projects, nameFilter)
                this.projectCount = values.ProjectCount
                if (values.StageSets) {
                    this.stageSets = values.StageSets
                }
                this.canLoadNextProjects = values.Projects.length === 30
            } catch (e) {
                console.error(e)
            } finally {
                this.isProjectsLoading = false
            }
        },
        async getFilteredProjectsPartion(nameFilter: string = '') {
            if (this.canLoadNextProjects) {
                try {
                    this.isProjectsLoading = true
                    const clients = this.buildSearchClients()
                    const values = await SearchApi.getProjectsPartion(this.selectedStages, this.selectedTags, nameFilter, this.projects[this.projects.length - 1].CreateTime?.split('+')[0], clients)
                    this.projects = [...this.projects, ...highlightProjects(values.Projects, nameFilter)]
                    this.canLoadNextProjects = values.Projects.length === 30
                } catch (e) {
                    console.error(e)
                } finally {
                    this.isProjectsLoading = false
                }
            }
        },
        async loadClientFilterList(type: 'client' | 'project', entityId: number) {
            try {
                this.isClientFilterLoading = true
                let list: ClientFilterOption[]
                if (type === 'project') {
                    // на странице Проекта приходит массив
                    const res = await ClientsApi.getProjectFilter(entityId)
                    list = res || []
                } else {
                    // на странице Клиента приходит ОДИН объект, а не массив
                    const res = await ClientsApi.getClientFilter(entityId)
                    list = res ? [res] : []
                }
                this.clientFilterList = list.map(o => ({ ...o, Union: o.Union ?? [] }))
                this.selectedClients = []
                this.byBase = type === 'project'
            } catch (e) {
                console.error(e)
                this.clientFilterList = []
                this.selectedClients = []
                this.byBase = type === 'project'
            } finally {
                this.isClientFilterLoading = false
            }
        },
        preselectClient(client: { Id: number, Name: string }) {
            if (this.byBase || this.selectedClients.some(c => c.Id === client.Id)) {
                return
            }
            const fromList = this.clientFilterList.find(o => o.Id === client.Id)
            this.selectedClients = [{
                Id: client.Id,
                Name: fromList?.Name ?? client.Name,
                UseUnion: false,
                Roles: [],
            }]
        },
        preselectMainProjectClient() {
            const main = this.clientFilterList[0]
            if (!main) {
                this.byBase = true
                this.selectedClients = []
                return
            }
            this.byBase = false
            this.selectedClients = [{
                Id: main.Id,
                Name: main.Name,
                UseUnion: false,
                Roles: [],
            }]
        },
        setByBase(value: boolean) {
            this.byBase = value
            if (value) {
                this.selectedClients = []
            }
        },
        // Верхнеуровневая строка — «клиент со всем холдингом». Включение затирает всё, что было
        // выбрано по этой опции, и кладёт ОДНУ запись: именно она уходит на бэк с useUnion=true.
        toggleFilterOption(opt: ClientFilterOption, on: boolean) {
            const memberIds = new Set(opt.Union.map(m => m.Id))
            this.selectedClients = this.selectedClients.filter(c =>
                c.Id !== opt.Id && !(memberIds.has(c.Id) && !c.UseUnion)
            )
            if (!on) {
                return
            }
            this.byBase = false
            this.selectedClients = [...this.selectedClients, {
                Id: opt.Id,
                Name: opt.Name,
                UseUnion: opt.Union.length > 0,
                Roles: [],
            }]
        },
        // Вложенная строка. Если опция была схлопнута в «холдинг целиком» — сначала разворачиваем
        // её в отдельных участников (роли при этом сбрасываются), затем добавляем/снимаем одного.
        // Если после этого выбранными оказались все участники — схлопываем обратно.
        toggleFilterMember(opt: ClientFilterOption, member: ClientFilterUnion, on: boolean) {
            const memberIds = opt.Union.map(m => m.Id)
            const isCollapsed = this.selectedClients.some(c => c.Id === opt.Id && c.UseUnion)

            if (isCollapsed) {
                const rest = this.selectedClients.filter(c => !(c.Id === opt.Id && c.UseUnion))
                // добавляем только тех, кого ещё нет: участник мог быть выбран отдельно
                // в рамках другой опции (страница Проекта), иначе получим дубль записи
                const missing = opt.Union
                    .filter(m => !rest.some(c => c.Id === m.Id && !c.UseUnion))
                    .map(m => ({ Id: m.Id, Name: m.Name, UseUnion: false, Roles: [] }))
                this.selectedClients = [...rest, ...missing]
            }

            if (on) {
                this.byBase = false
                if (!this.selectedClients.some(c => c.Id === member.Id && !c.UseUnion)) {
                    this.selectedClients = [...this.selectedClients, {
                        Id: member.Id,
                        Name: member.Name,
                        UseUnion: false,
                        Roles: [],
                    }]
                }
            } else {
                this.selectedClients = this.selectedClients.filter(c => !(c.Id === member.Id && !c.UseUnion))
            }

            const allSelected = memberIds.length > 0 && memberIds.every(id =>
                this.selectedClients.some(c => c.Id === id && !c.UseUnion)
            )
            if (allSelected) {
                this.selectedClients = [
                    ...this.selectedClients.filter(c => !(memberIds.includes(c.Id) && !c.UseUnion)),
                    { Id: opt.Id, Name: opt.Name, UseUnion: true, Roles: [] },
                ]
            }
        },
        removeSelectedClient(id: number) {
            this.selectedClients = this.selectedClients.filter(c => c.Id !== id)
        },
        attachRoleToSelectedClient(id: number, role: CommonEntityRecord) {
            const client = this.selectedClients.find(c => c.Id === id)
            if (!client) {
                return
            }
            if (!client.Roles.some(r => r.Id === role.Id)) {
                client.Roles = [...client.Roles, { Id: role.Id, Name: role.Name }]
            }
        },
        detachRoleFromSelectedClient(id: number, roleId: number) {
            const client = this.selectedClients.find(c => c.Id === id)
            if (!client) {
                return
            }
            client.Roles = client.Roles.filter(r => r.Id !== roleId)
        },
        async loadActualRoles() {
            if (this.actualRoles.length) {
                return
            }
            try {
                this.actualRoles = await ClientsApi.getActualRoles()
            } catch (e) {
                console.error(e)
            }
        },
        clearClientFilter() {
            this.clientFilterList = []
            this.selectedClients = []
            this.byBase = false
        },
        removeProjectFromList(projectId: number) {
            const idx = this.projects.findIndex(p => p.Id === projectId)
            if (idx !== -1) {
                this.projects.splice(idx, 1)
                if (this.projectCount > 0) {
                    this.projectCount -= 1
                }
            }
        },
        async setSelectedStage(stage: number, nameFilter: string = '') {
            this.canLoadNextProjects = true
            this.selectedStages.includes(stage)
                ? this.selectedStages.splice(this.selectedStages.findIndex(s => s === stage), 1)
                : this.selectedStages.push(stage)

            await this.getFilteredProjects(nameFilter)
        },
        dropTags() {
            this.selectedStages = []
            this.selectedTags = []
        }
    },
    getters: {
        clientsLoading(state) {
            return state.isClientsLoading
        },
        projectsLoading(state) {
            return state.isProjectsLoading
        },
        clientsCounter(state) {
            return state.clientsCount
        },
        allClients(state) {
            return state.clients
        },
        allClientsHighlighted(state) {
            return state.clientsHighlighted
        },
        allProjects(state): Project[] {
            return state.projects
        },
        projectCounter(state) {
            return state.projectCount
        },
        projectsStagesList(state) {
            return state.stageSets.map(stage => ({
                Id: stage.Id,
                Name: stage.Name,
            }))
        },
        stageItemsForSelectedStage(state) {
            if (!state.selectedStage) {
                return []
            }

            return state.stageSets.find(stage => stage.Id === state.selectedStage)?.Stages ?? []
        },
        allSelectedStages(state) {
            return state.selectedStages
        },
        allSelectedTags(state) {
            return state.selectedTags
        }
    }
})

export type Client = {
    Id: number,
    Name: string
}

export type SearchedClient = {
    AddressChain: string[] | null,
    Clients: Client[] | null,
    CreateTime: string,
    Id: number,
    Name: string,
    Parameters: Parameter[],
    Tags: {
        Type: number,
        Id: number,
        Name: string
    }[]
}

type Instance = {
    IsRecord: boolean,
    Key: string,
    RecordFields: any,
    Value: string
}

export type Parameter = {
    Instance: Instance,
    ListTitle: any,
    Type: number,
    UsedInSearch: boolean
}

export type Tag = {
    Id: number,
    Name: string,
    Type: number
}
